import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { publicApi } from "../api/api";
import styles from "../styles/ProductGrid.module.css";
import { getLandingImages, LANDING_IMAGE_TAG } from "../utils/landingImages";

// The list is requested with ?image_tag=website, so image_details only has website-tagged images.
// Card image = the one that also carries the "main" tag, else the first website image. With no
// website-tagged images (or an older API) it falls back to images[mainImageIndex], then images[0].
const getMainImage = (product) => {
  const { images, mainIndex } = getLandingImages(product);
  return images[mainIndex] || null;
};

const ProductGrid = ({
  serviceId, 
  title = "Products", 
  defaultPageSize = 20,
  showPagination = true,
  gridClassName = "",
  onProductClick = null,
  defaultImage = null,
  useProductImages = true,
  variant = "grid", // "grid" (default) | "carousel" (home page: horizontal cards + View All)
  viewAllLink = null
}) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  useEffect(() => {
    if (!serviceId) return;
    
    setLoading(true);
    setError(null);
    publicApi
      .get(`/products/${serviceId}/list`, { params: { image_tag: LANDING_IMAGE_TAG } })
      .then((res) => {
        const products = Array.isArray(res.data.data) ? res.data.data : [];
        setProducts(products);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load products. Please try again later.");
        setLoading(false);
      });
  }, [serviceId]);

  const handleProductClick = (product) => {
    if (onProductClick) {
      onProductClick(product);
    } else {
      navigate(`/landing/${product.sku}`);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(products.length / pageSize) || 1;
  const paginatedProducts = products.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const renderPagination = () => {
    if (!showPagination || products.length <= pageSize) return null;

    return (
      <div className={styles["pagination-controls"]}>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className={styles["pagination-btn"]}
        >
          Prev
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
          <button
            key={num}
            onClick={() => setPage(num)}
            disabled={num === page}
            className={
              styles["pagination-btn"] + (num === page ? " " + styles["active"] : "")
            }
          >
            {num}
          </button>
        ))}
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          className={styles["pagination-btn"]}
        >
          Next
        </button>
        <div className={styles["pagination-rows"]}>
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Rows per page"
            className={styles["pagination-select"]}
          >
            {[4, 8, 12, 20].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  if (variant === "carousel" || variant === "cards") {
    // carousel = home page (horizontal scroll); cards = service page (same cards, wrapping grid)
    const isCards = variant === "cards";
    const visibleProducts = isCards ? paginatedProducts : products;
    return (
      <div className={styles["carousel-section"]}>
        <div className={styles["carousel-header"]}>
          {title && <h2 className={styles["carousel-title"]}>{title}</h2>}
          {viewAllLink && !loading && !error && products.length > 0 && (
            <button
              type="button"
              className={styles["carousel-view-all"]}
              onClick={() => navigate(viewAllLink)}
            >
              View All ›
            </button>
          )}
        </div>
        {loading ? (
          <div className={styles["carousel-message"]}>Loading ...</div>
        ) : error ? (
          <div className={styles["error-message"]}>{error}</div>
        ) : products.length === 0 ? (
          <div className={styles["carousel-message"]}>Coming soon</div>
        ) : (
          <div className={styles[isCards ? "cards-grid" : "carousel-track"]}>
            {visibleProducts.map((product) => {
              const mainImage = useProductImages ? getMainImage(product) : null;
              const imgSrc = mainImage || defaultImage;
              return (
                <div
                  key={product.sku}
                  className={styles["carousel-card"]}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleProductClick(product)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleProductClick(product);
                    }
                  }}
                >
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={product.name}
                      className={styles["carousel-card-img"]}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles["carousel-card-placeholder"]}>?</div>
                  )}
                  <div className={styles["carousel-card-name"]}>
                    {product.tagline || product.name}
                  </div>
                  <button
                    type="button"
                    className={styles["carousel-card-btn"]}
                    onClick={(e) => {
                      e.stopPropagation(); // the card itself is also clickable
                      handleProductClick(product);
                    }}
                  >
                    Buy Now
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {isCards && !loading && !error && products.length > 0 && renderPagination()}
      </div>
    );
  }

  return (
    <div className={styles["product-grid-container"]}>
      {title && <h1 className={styles["product-grid-title"]}>{title}</h1>}
      <div className={`${styles["question-grid-custom"]} ${gridClassName}`}>
        {loading ? (
          <div>Loading ...</div>
        ) : error ? (
          <div className={styles["error-message"]}>{error}</div>
        ) : products.length === 0 ? (
          <div className={styles["coming-soon-container"]}>
            <div className={styles["coming-soon-content"]}>
              <div className={styles["coming-soon-icon"]}>🚀</div>
              <h3 className={styles["coming-soon-title"]}>Coming Soon</h3>
              <p className={styles["coming-soon-message"]}>
                We're working on bringing you amazing products in this category.
                <br />
                Stay tuned for exciting updates!
              </p>
            </div>
          </div>
        ) : (
          paginatedProducts.map((product) => {
            const mainImage = useProductImages ? getMainImage(product) : null;
            return (
            <div
              key={product.sku}
              className={styles["product-thumbnail-custom"]}
              tabIndex={0}
            >
              {mainImage ? (
                <img
                  src={mainImage}
                  alt={product.name}
                  className={styles["product-thumbnail-img"]}
                />
              ) : defaultImage ? (
                <img
                  src={defaultImage}
                  alt={product.name}
                  className={styles["product-thumbnail-img"]}
                />
              ) : (
                <div className={styles["product-thumbnail-placeholder"]}>?</div>
              )}
              <div className={styles["product-thumbnail-name"]}>
                {product.tagline || product.name}
              </div>
              <div className={styles["product-thumbnail-desc"]}>
                
              </div>
              <button
                className={styles["product-thumbnail-btn"]}
                onClick={() => handleProductClick(product)}
              >
                Buy Now
              </button>
            </div>
            );
          })
        )}
      </div>
      {/* Only show pagination when there are products */}
      {!loading && !error && products.length > 0 && renderPagination()}
    </div>
  );
};

export default ProductGrid;
